import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    IconButton,
    Grid,
    Button,
    Card,
    CardContent,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Tooltip,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import HistoryIcon from '@mui/icons-material/History';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

import { PageHeaderCard, TableSkeleton, StatCardSkeleton } from '../../components/common';
import { weeklyReportsAPI } from '../../services/api';
import { formatDate, formatDateTime } from '../../utils/helper';

const WeeklyReportHistory = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [isLoading, setIsLoading] = useState(true);
    const [reports, setReports] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [entryFilter, setEntryFilter] = useState('all');

    useEffect(() => {
        let isMounted = true;

        const load = async () => {
            setIsLoading(true);
            try {
                const data = await weeklyReportsAPI.getPublishedHistory();
                if (!isMounted) {
                    return;
                }
                setReports(Array.isArray(data) ? data : []);
            } catch {
                if (!isMounted) {
                    return;
                }
                setReports([]);
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        load();
        return () => {
            isMounted = false;
        };
    }, []);

    const stats = useMemo(() => {
        const total = reports.length;
        const totalEntries = reports.reduce((sum, report) => sum + (report.entries?.length || 0), 0);
        const avgEntriesPerReport = total > 0 ? Math.round(totalEntries / total) : 0;

        return { total, totalEntries, avgEntriesPerReport };
    }, [reports]);

    const filteredReports = useMemo(() => {
        return reports.filter((report) => {
            if (dateFrom && new Date(report.weekStart) < new Date(dateFrom)) {
                return false;
            }
            if (dateTo && new Date(report.weekEnd) > new Date(dateTo)) {
                return false;
            }

            const entryCount = report.entries?.length || 0;
            if (entryFilter === '1-5' && (entryCount < 1 || entryCount > 5)) {
                return false;
            }
            if (entryFilter === '6-10' && (entryCount < 6 || entryCount > 10)) {
                return false;
            }
            if (entryFilter === '10+' && entryCount < 11) {
                return false;
            }

            return true;
        });
    }, [reports, dateFrom, dateTo, entryFilter]);

    const paginatedReports = useMemo(() => {
        const start = page * rowsPerPage;
        return filteredReports.slice(start, start + rowsPerPage);
    }, [filteredReports, page, rowsPerPage]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleViewReport = (report) => {
        navigate(`/reports/weekly?weekStart=${report.weekStart}`);
    };

    const handleClearFilters = () => {
        setDateFrom('');
        setDateTo('');
        setEntryFilter('all');
        setPage(0);
    };

    const hasFilters = dateFrom || dateTo || entryFilter !== 'all';

    return (
        <Box>
            <PageHeaderCard
                title="Weekly Report History"
                subtitle="Browse your published weekly reports"
                icon={<HistoryIcon sx={{ color: 'primary.main', fontSize: { xs: 22, sm: 26 } }} />}
            />

            {isLoading ? (
                <StatCardSkeleton count={3} />
            ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
                    {[
                        { title: 'Published Reports', value: stats.total, icon: CheckCircleIcon, color: '#2e7d32' },
                        { title: 'Total Entries', value: stats.totalEntries, icon: AssignmentIcon, color: '#0288d1' },
                        { title: 'Avg Entries / Report', value: stats.avgEntriesPerReport, icon: CalendarMonthIcon, color: '#7b1fa2' },
                    ].map((item) => {
                        const IconComponent = item.icon;
                        return (
                            <Card
                                key={item.title}
                                elevation={0}
                                sx={{
                                    border: '1px solid',
                                    borderColor: 'grey.200',
                                    borderRadius: 2,
                                }}
                            >
                                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                                {item.title}
                                            </Typography>
                                            <Typography variant="h5" fontWeight={700}>
                                                {item.value}
                                            </Typography>
                                        </Box>
                                        <Box
                                            sx={{
                                                p: 1,
                                                borderRadius: 1.5,
                                                backgroundColor: `${item.color}12`,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <IconComponent sx={{ color: item.color, fontSize: 20 }} />
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        );
                    })}
                </Box>
            )}

            <Card
                elevation={0}
                sx={{
                    mb: 3,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'grey.200',
                }}
            >
                <Box sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <FilterListIcon color="action" sx={{ fontSize: 20 }} />
                        <Typography variant="subtitle2" fontWeight={600}>
                            Filters
                        </Typography>
                        {hasFilters && (
                            <Chip
                                label={`${filteredReports.length} results`}
                                size={isMobile ? 'small' : 'medium'}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                    </Box>

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={3}>
                                <DatePicker
                                    label="From Date"
                                    value={dateFrom ? dayjs(dateFrom) : null}
                                    format="DD MMM YYYY"
                                    maxDate={dateTo ? dayjs(dateTo) : undefined}
                                    onChange={(value) => {
                                        setDateFrom(value ? value.format('YYYY-MM-DD') : '');
                                        setPage(0);
                                    }}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            size: 'small',
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <DatePicker
                                    label="To Date"
                                    value={dateTo ? dayjs(dateTo) : null}
                                    format="DD MMM YYYY"
                                    minDate={dateFrom ? dayjs(dateFrom) : undefined}
                                    onChange={(value) => {
                                        setDateTo(value ? value.format('YYYY-MM-DD') : '');
                                        setPage(0);
                                    }}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            size: 'small',
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Entries</InputLabel>
                                    <Select
                                        value={entryFilter}
                                        label="Entries"
                                        onChange={(event) => {
                                            setEntryFilter(event.target.value);
                                            setPage(0);
                                        }}
                                    >
                                        <MenuItem value="all">All</MenuItem>
                                        <MenuItem value="1-5">1 - 5</MenuItem>
                                        <MenuItem value="6-10">6 - 10</MenuItem>
                                        <MenuItem value="10+">11+</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    startIcon={<ClearIcon />}
                                    onClick={handleClearFilters}
                                    disabled={!hasFilters}
                                >
                                    Clear Filters
                                </Button>
                            </Grid>
                        </Grid>
                    </LocalizationProvider>
                </Box>
            </Card>

            <Card
                elevation={0}
                sx={{
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'grey.200',
                    overflow: 'hidden',
                }}
            >
                {isLoading ? (
                    <Box sx={{ p: 3 }}>
                        <TableSkeleton columns={5} rows={5} />
                    </Box>
                ) : (
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: 'grey.50' }}>
                                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Week Period</TableCell>
                                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Title</TableCell>
                                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Entries</TableCell>
                                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Published At</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedReports.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                                            <HistoryIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1.5 }} />
                                            <Typography color="text.secondary">No published weekly reports found</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    paginatedReports.map((report, index) => (
                                        <TableRow
                                            key={report.id}
                                            hover
                                            sx={{
                                                backgroundColor: index % 2 === 0 ? 'transparent' : 'grey.50',
                                            }}
                                        >
                                            <TableCell>
                                                <Typography fontWeight={500} sx={{ fontSize: '0.9rem' }}>
                                                    {formatDate(report.weekStart)} - {formatDate(report.weekEnd)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography sx={{ fontSize: '0.9rem' }}>
                                                    {report.title || 'Weekly Activity Report'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    size={isMobile ? 'small' : 'medium'}
                                                    label={`${report.entries?.length || 0} entries`}
                                                    color="primary"
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography sx={{ fontSize: '0.85rem' }}>
                                                    {formatDateTime(report.publishedAt)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Tooltip title="Open week detail">
                                                    <IconButton color="primary" onClick={() => handleViewReport(report)}>
                                                        <VisibilityIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={filteredReports.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Card>
        </Box>
    );
};

export default WeeklyReportHistory;
